import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useState } from 'react';
import { useIsGlobetrotter } from '@bwoty-web/ui-kit/hooks';
import { Accordion, AccordionItem, Button, Modal, Tag } from '@bwoty-web/ui-kit';

import FlightDetails from '../../shared/flightDetails/FlightDetails';
import FlightListFlight from '../../shared/flightListItem/FlightListFlight';
import { urlUtils } from '../../../utils/url';

import { getFlightTime as hotelListGetFlightTime } from '../../../redux/actions/hotelListActions';
import { getFlightTime as priceMatrixGetFlightTime } from '../../../redux/actions/priceMatrixActions';

import './flightInfo.scss';

// **This component reads two different stores**, hotel-list's and price-matrix's, which keep
// their labels and transport in different shapes. `inPriceMatrix` is the only thing that
// distinguishes them, and it must stay a prop: both stores are plain objects, so nothing about
// the state itself can tell them apart. The selectors are inline for the same reason — neither
// view's selector module fits both shapes.
const FlightInfo = ({ inPriceMatrix = false }) => {
  const dispatch = useDispatch();
  const labels = useSelector((state) => (inPriceMatrix ? state.labels.priceMatrix : state.labels));
  const transportInfo = useSelector((state) => state.transport || state.transportData);
  const selectedRoomGroup = useSelector((state) => (inPriceMatrix ? state?.hotelData?.selectedRoomGroup : null));

  const isGlobetrotter = useIsGlobetrotter();

  const [isDetailsOpen, setDetailsPopUpState] = useState(false);

  if (!transportInfo.initialized) {
    return null;
  }

  const firstOutBoundFlight = transportInfo.outBoundFlights[0];

  const firstHomeBoundFlight = transportInfo.homeBoundFlights[0];
  const lastHomeBoundFlight = transportInfo.homeBoundFlights.slice(-1)[0];

  const openDetails = () => {
    setDetailsPopUpState(true);
    const getFlightTimeAction = inPriceMatrix ? priceMatrixGetFlightTime : hotelListGetFlightTime;

    dispatch(getFlightTimeAction(lastHomeBoundFlight.bookingEngineKey, firstOutBoundFlight.bookingEngineKey));
  };

  const getChangeFlightUrl = () => {
    if (!inPriceMatrix) {
      return urlUtils.standaloneUrlHandler(`${transportInfo.flightListUrl}&${urlUtils.getFilterQueriesAsString()}`);
    }

    if (!transportInfo.navigationUrl) return '';

    const roomGroupQuery = selectedRoomGroup ? `&RoomGroupKey=${selectedRoomGroup.bookingEngineRoomGroupKey}` : '';
    return urlUtils.standaloneUrlHandler(`/${transportInfo.navigationUrl}${roomGroupQuery}`);
  };

  const changeFlightUrl = getChangeFlightUrl();

  const changeFlightOnClickHandler = () => {
    window.dataLayer.push({
      event: 'flight_list',
      eventAction: 'click',
      eventInfo: 'more_flights',
    });

    return true;
  };

  return (
    <div className="iw-flights-info-wrapper">
      <div>
        <Accordion
          className="iw-flight-info__accordion"
          oneAtATime
        >
          <AccordionItem
            header={
              <div className="iw-flights-info__accordion-item-header-container">
                <p>{labels.yourFlightDetails}</p>
                <Tag
                  className="iw-flights-info__accordion-item-header-tag"
                  small
                  variant="info"
                >
                  {labels?.dpTravel}
                </Tag>
              </div>
            }
            initiallyExpanded
          >
            <div className="iw-flights-info">
              <div className="iw-flight-info-mobile">
                <div>
                  <div className="iw-flight-info-mobile__departure-date-container">
                    <p>{labels.outboundShort}</p>
                    <strong>{firstOutBoundFlight.departureTime}</strong>
                    <p>{firstOutBoundFlight.departureDate}</p>
                  </div>
                  <p>{firstOutBoundFlight.carrierName}</p>
                </div>
                <div>
                  <div className="iw-flight-info-mobile__departure-date-container">
                    <p>{labels.homeboundShort}</p>
                    <strong>{firstHomeBoundFlight.departureTime}</strong>
                    {/* TODO: Check if we use -margin for fullWidth, no needed for this then */}
                    <p style={{ textWrap: 'nowrap' }}>{firstHomeBoundFlight.departureDate}</p>
                  </div>
                  <p>{firstHomeBoundFlight.carrierName}</p>
                </div>
              </div>
              <div className="iw-flights-info__flights">
                <FlightListFlight
                  flightInfo={firstOutBoundFlight}
                  labels={labels}
                  openDetails={(index) => openDetails(index)}
                />
                <FlightListFlight
                  flightInfo={firstHomeBoundFlight}
                  labels={labels}
                  openDetails={(index) => openDetails(index)}
                  isHomeBound
                />
              </div>
              {changeFlightUrl && (
                <div className="iw-flights-info__button-panel">
                  <div className="iw-flights-info__button-panel-label">{labels.changeFlightDescription}</div>
                  <Button
                    className="iw-flights-info__change-flight-button"
                    href={changeFlightUrl}
                    onClick={changeFlightOnClickHandler}
                    size={isGlobetrotter ? 's' : undefined}
                  >
                    {labels.flightOption_all}
                  </Button>
                </div>
              )}
            </div>
          </AccordionItem>
        </Accordion>
      </div>
      <Modal
        heading={labels.flightInformationLabel}
        isOpen={isDetailsOpen}
        onClose={() => setDetailsPopUpState(false)}
      >
        <FlightDetails
          flights={[...transportInfo.outBoundFlights, ...transportInfo.homeBoundFlights]}
          flightTimeInformation={transportInfo.flightTimeInformation}
          labels={labels}
        />
      </Modal>
    </div>
  );
};

FlightInfo.propTypes = {
  // The parent says which store this is reading — see the note above the component.
  inPriceMatrix: PropTypes.bool,
};

export default FlightInfo;
